import { Flex, Tag, Typography } from 'antd';
import { ArrowDown, Factory, MapPin, Truck } from '@phosphor-icons/react';
import type { ShipmentListType } from 'schemas/shipment/inbound/list';
import type { InboundShipmentColumnKey } from '@utility/constants/inbound-shipment-columns';
import {
  getShipmentStatusMeta,
  type InboundListDisplay,
} from '@utility/utils/shipment-display';
import { COLOR } from '@styles/theme';
import { DateUtility, DateFormat } from '@utility/date';
import { CommonUtility } from '@utility/common';
import { Flag } from '../../../elements/Flag';
import { LabelValue } from './LabelValue';
import { ProductThumbsCell } from './ProductThumbsCell';

const { Text } = Typography;

function formatDateNumeric(iso?: string | null) {
  if (!iso) return '—';
  return DateUtility.formatDate(iso, DateFormat.date);
}

export function renderInboundShipmentCell(
  key: InboundShipmentColumnKey,
  row: ShipmentListType,
  display: InboundListDisplay,
) {
  switch (key) {
    case 'products':
      return <ProductThumbsCell row={row} />;
    case 'shipment':
      return (
        <Flex vertical gap={8}>
          <LabelValue label="ID" value={row.display_id || '—'} />
          <LabelValue label="Customer" value={display.customerName} />
        </Flex>
      );
    case 'contents':
      return (
        <Flex vertical gap={8}>
          <Flex gap={24}>
            <LabelValue
              label="Cartons"
              value={CommonUtility.decimalWithCommas(
                CommonUtility.roundNumber(display.cartons),
              )}
            />
            <LabelValue
              label="Units"
              value={CommonUtility.decimalWithCommas(
                CommonUtility.roundNumber(display.units),
              )}
            />
          </Flex>
          <LabelValue
            label="Gross Weight"
            value={`${CommonUtility.decimalWithCommas(CommonUtility.roundNumber(display.grossWeight))} kg`}
          />
        </Flex>
      );
    case 'route':
      return (
        <Flex vertical gap={6}>
          <Flex align="center" gap={6}>
            <Truck size={16} color={COLOR.textSecondary} />
            <Text strong style={{ fontSize: 14 }}>
              {CommonUtility.removeUnderscoreAndCapitalize(row.shipping_type) || '—'}
            </Text>
          </Flex>
          <Flex align="center" gap={6}>
            <Factory size={16} color={COLOR.textSecondary} />
            <Flag name={display.supplierCountry} />
            <Text strong style={{ fontSize: 14 }}>
              {display.supplierName}
            </Text>
          </Flex>
          <Flex justify="flex-start" style={{ width: '100%' }}>
            <ArrowDown size={16} weight="bold" color={COLOR.textSecondary} />
          </Flex>
          <Flex align="center" gap={6}>
            <MapPin size={16} color={COLOR.textSecondary} />
            <Flag name={display.warehouseCountry} />
            <Text strong style={{ fontSize: 14 }}>
              {display.warehouseName}
            </Text>
          </Flex>
        </Flex>
      );
    case 'status': {
      const meta = getShipmentStatusMeta(row.status);
      return (
        <Flex vertical gap={8} align="flex-start">
          <LabelValue
            label="Status"
            value={
              <Tag color={meta.color} style={{ margin: 0 }}>
                {meta.label}
              </Tag>
            }
          />
          <LabelValue label="ETD" value={formatDateNumeric(row.ETD)} />
          <LabelValue label="ETA" value={formatDateNumeric(row.ETA)} />
        </Flex>
      );
    }
    default:
      return null;
  }
}
